import React, { useState, useRef, useEffect } from 'react';
import {
  X,
  Upload,
  Video,
  Youtube,
  FolderOpen,
  FileSpreadsheet,
  Cloud,
  FileVideo,
  Loader,
  CheckCircle2,
  Play,
  CloudUpload,
  AlertTriangle
} from 'lucide-react';
import { useMsalAuth } from './MsalProvider';
import OneDrivePicker from './OneDrivePicker';
import OneDriveService from '../utils/oneDriveService';

import FolderDropdown from './FolderDropdown';
import { getUser, formatFileSize, successMessage, errorMessage } from '../utils/helpers';
import { analyzeVideo, analyzeMultipleVideos } from '../utils/videoAnalyticsApi';
import { getMBBRoasters } from '../utils/MBBApis';
import apiClient from '../utils/apiClient';
import { getSelectedSport } from '../utils/helpers';
import { useLocation } from 'react-router-dom';

const BioMechanicsDrawer = ({ open, onClose, currentFolder, currentFolderId, onUploadComplete }) => {
  const isDarkMode = true; // OLED: always dark
  const location = useLocation();
  const [inputType, setInputType] = useState('upload');
  const [videoFiles, setVideoFiles] = useState([]);
  const [videoPreviews, setVideoPreviews] = useState([]);
  const [myVideos, setMyVideos] = useState([]);
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isDownloadingYoutube, setIsDownloadingYoutube] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [error, setError] = useState(null);
  const [playerIdForAnalysis, setPlayerIdForAnalysis] = useState('');
  const [additionalInstructions, setAdditionalInstructions] = useState('Analyze this video');
  const [dropdownFolderId, setDropdownFolderId] = useState(null);
  const [allPlayers, setAllPlayers] = useState([]);
  const fileInputRef = useRef(null);
  const oneDrivePickerRef = useRef(null);
  const { instance, isAuthenticated, isInitialized } = useMsalAuth();
  const [oneDriveService, setOneDriveService] = useState(null);
  const [isDownloadingOneDriveFiles, setIsDownloadingOneDriveFiles] = useState(false);
  const [oneDriveDownloads, setOneDriveDownloads] = useState({});

  useEffect(() => {
    fetchPlayers();

    // Log the current folder when it changes
    if (currentFolder) {
      setDropdownFolderId(currentFolderId)
      console.log('Current folder in drawer:', currentFolder);
    }
    const params = new URLSearchParams(location.search);

    const player_id = params.get('player_id');
    if (player_id) {
      setPlayerIdForAnalysis(player_id);
    }
  }, [currentFolder]);

  // Initialize OneDrive service when needed and authenticated
  useEffect(() => {
    if (instance && isInitialized && isAuthenticated) {
      try {
        console.log('Creating OneDriveService in BioMechanicsDrawer');
        const service = new OneDriveService(instance);
        setOneDriveService(service);
      } catch (error) {
        console.error('Failed to create OneDriveService:', error);
      }
    }
  }, [instance, isInitialized, isAuthenticated]);

  const fetchPlayers = async () => {
    try {
      const savedTeam = sessionStorage.getItem('selectedTeam');
      const team = savedTeam ? JSON.parse(savedTeam) : null;

      if (!team?.team_id) {
        console.warn('No team selected');
        return;
      }
      let response;
      if (getSelectedSport() === 'MBB') {
        response = await getMBBRoasters(team?.name);
      } else {
        response = await apiClient.get(`${getSelectedSport()}/players/`, {
          params: {
            team_id: team.team_id,
            skip: 0,
            limit: 100
          }
        });
      }
      setAllPlayers(response?.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  const handleFileChange = (e) => {
    let validVideoFiles = [];
    let validCsvFiles = [];
    let validPreviews = [];
    let errors = [];

    try {
      setError(null);

      if (!e.target.files || e.target.files.length === 0) {
        setError('No files selected. Please choose at least one file.');
        return;
      }

      const files = Array.from(e.target.files);

      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
      const SUPPORTED_VIDEO_TYPES = ['video/mp4', 'video/avi', 'video/mov'];
      const SUPPORTED_VIDEO_EXTENSIONS = ['mp4', 'avi', 'mov'];
      const SUPPORTED_CSV_TYPES = ['text/csv', 'application/csv'];

      const existingCsvFile = myVideos.find(file => file.type === 'csv');

      files.forEach((file, index) => {
        try {
          if (!file || !file.name || file.size === undefined) {
            errors.push(`File ${index + 1}: Invalid file object.`);
            return;
          }

          if (file.size === 0) {
            errors.push(`"${file.name}": File is empty.`);
            return;
          }

          if (file.size > MAX_FILE_SIZE) {
            errors.push(`"${file.name}": File size (${(file.size / 1024 / 1024).toFixed(2)}MB) exceeds maximum limit of ${MAX_FILE_SIZE / 1024 / 1024}MB.`);
            return;
          }

          const fileName = file.name.trim();
          if (!fileName || fileName.length === 0) {
            errors.push(`File ${index + 1}: Invalid file name.`);
            return;
          }

          const nameParts = fileName.split('.');
          if (nameParts.length < 2) {
            errors.push(`"${fileName}": File has no extension.`);
            return;
          }

          const fileExtension = nameParts.pop().toLowerCase();
          if (!fileExtension) {
            errors.push(`"${fileName}": Invalid file extension.`);
            return;
          }

          if (fileName.includes('..') || fileName.includes('/') || fileName.includes('\\')) {
            errors.push(`"${fileName}": Invalid characters in file name.`);
            return;
          }

          if (file.type.startsWith('video/') || SUPPORTED_VIDEO_EXTENSIONS.includes(fileExtension)) {
            const isValidType = SUPPORTED_VIDEO_TYPES.includes(file.type);
            const isValidExtension = SUPPORTED_VIDEO_EXTENSIONS.includes(fileExtension);

            if (!isValidType && !isValidExtension) {
              errors.push(`"${fileName}": Unsupported video format. Only MP4, AVI, and MOV files are supported.`);
              return;
            }

            const isDuplicateInCurrent = validVideoFiles.some(existingFile => existingFile.name === fileName);
            const isDuplicateInExisting = videoFiles && videoFiles.some(existingFile => existingFile.name === fileName);

            if (isDuplicateInCurrent || isDuplicateInExisting) {
              errors.push(`"${fileName}": A video file with this name already exists.`);
              return;
            }

            validVideoFiles.push(file);

            try {
              const previewUrl = URL.createObjectURL(file);
              validPreviews.push(previewUrl);
            } catch (urlError) {
              console.error('Failed to create preview URL for', fileName, urlError);
              errors.push(`"${fileName}": Failed to create preview.`);
              return;
            }
          }
          else if (fileExtension === 'csv' || SUPPORTED_CSV_TYPES.includes(file.type)) {
            if (existingCsvFile) {
              errors.push('Only one CSV file is allowed. Please remove the existing CSV file first.');
              return;
            }

            if (validCsvFiles.length > 0) {
              errors.push(`Only one CSV file is allowed. "${fileName}" was skipped.`);
              return;
            }

            const isDuplicateCsv = myVideos.some(existingFile =>
              existingFile.name === fileName && existingFile.type === 'csv'
            );
            if (isDuplicateCsv) {
              errors.push(`"${fileName}": A CSV file with this name already exists.`);
              return;
            }

            const CSV_MAX_SIZE = 10 * 1024 * 1024;
            if (file.size > CSV_MAX_SIZE) {
              errors.push(`"${fileName}": CSV file too large. Maximum size is ${CSV_MAX_SIZE / 1024 / 1024}MB.`);
              return;
            }

            validCsvFiles.push(file);
          }
          else {
            errors.push(`"${fileName}": Unsupported file type. Only video files (MP4, AVI, MOV) and CSV files are accepted.`);
            return;
          }
        } catch (fileError) {
          console.error('Error processing file:', file?.name || 'Unknown', fileError);
          errors.push(`"${file?.name || 'Unknown file'}": Error processing file - ${fileError.message}`);
        }
      });

      if (validVideoFiles.length === 0 && validCsvFiles.length === 0) {
        if (errors.length > 0) {
          setError(`No valid files to upload. Issues found:\n${errors.join('\n')}`);
        } else {
          setError('No valid files found. Please select video or CSV files.');
        }
        validPreviews.forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (revokeError) {
            console.error('Error revoking URL:', revokeError);
          }
        });
        return;
      }

      if (errors.length > 0) {
        console.warn('File upload warnings:', errors);
      }

      if (validVideoFiles.length > 0) {
        setVideoFiles(prevFiles => [...prevFiles, ...validVideoFiles]);
        setVideoPreviews(prevPreviews => [...prevPreviews, ...validPreviews]);
      }

      if (validCsvFiles.length > 0) {
        try {
          const processedCsvFiles = validCsvFiles.map(file => ({
            id: Date.now() + Math.random(),
            file: file,
            name: file.name,
            type: 'csv',
            size: formatFileSizeHelper(file.size),
            progress: 0,
            status: 'ready',
            isOneDrive: false
          }));

          setMyVideos(prevFiles => {
            const nonCsvFiles = prevFiles.filter(file => file.type !== 'csv');
            return [...nonCsvFiles, ...processedCsvFiles];
          });

          successMessage(`Added CSV file: ${validCsvFiles[0].name}`);
        } catch (csvError) {
          console.error('Error processing CSV files:', csvError);
          setError(`Error processing CSV file: ${csvError.message}`);
          return;
        }
      }

      if (validVideoFiles.length > 0 || validCsvFiles.length > 0) {
        setError(null);

        if (e.target) {
          e.target.value = '';
        }
      }

    } catch (globalError) {
      console.error('Unexpected error in file upload handler:', globalError);
      setError(`An unexpected error occurred while processing files: ${globalError.message}`);

      if (validPreviews && validPreviews.length > 0) {
        validPreviews.forEach(url => {
          try {
            URL.revokeObjectURL(url);
          } catch (revokeError) {
            console.error('Error revoking URL:', revokeError);
          }
        });
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const syntheticEvent = {
      target: {
        files: files
      }
    };
    handleFileChange(syntheticEvent);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleRemoveVideo = (index) => {
    setVideoFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
    setVideoPreviews(prevPreviews => {
      URL.revokeObjectURL(prevPreviews[index]);
      return prevPreviews.filter((_, i) => i !== index);
    });
  };

  // Handle OneDrive file selection
  const handleOneDriveSelect = (data) => {
    console.log('OneDrive selection data:', data);
    if (data?.action === 'cancel') {
      return;
    }

    if (data?.action === 'error') {
      setError(data.message || 'Error connecting to OneDrive');
      return;
    }

    if (data?.docs) {
      // Apply validation logic as normal file upload
      const selectedOneDriveFiles = data?.docs;

      // Filter video and CSV files
      const oneDriveVideoFiles = [];
      const oneDriveCsvFiles = [];

      // Check if we already have a CSV file
      const existingCsvFile = myVideos.find(file => file.type === 'csv');

      selectedOneDriveFiles.forEach(doc => {
        const extension = doc.name.split('.').pop().toLowerCase();
        if (['mp4', 'mov', 'avi'].includes(extension)) {
          oneDriveVideoFiles.push(doc);
        } else if (extension === 'csv') {
          // Only add the first CSV file if we don't already have one
          if (!existingCsvFile && oneDriveCsvFiles.length === 0) {
            oneDriveCsvFiles.push(doc);
          } else if (existingCsvFile) {
            setError('Only one CSV file is allowed. Please remove the existing CSV file first.');
          } else if (oneDriveCsvFiles.length > 0) {
            setError('Only one CSV file is allowed. Only the first CSV file will be used.');
          }
        }
      });

      if (oneDriveVideoFiles.length === 0 && oneDriveCsvFiles.length === 0) {
        setError('Please select video files (MP4, MOV, AVI) or CSV files from OneDrive');
        return;
      }

      // Process CSV files first
      if (oneDriveCsvFiles.length > 0) {
        // Process the CSV files
        const processedCsvFiles = oneDriveCsvFiles.map(doc => ({
          id: Date.now() + Math.random(),
          file: {
            name: doc.name,
            size: doc.size || 0,
            // Add OneDrive specific properties
            oneDriveId: doc.id,
            webUrl: doc.webUrl,
            mimeType: 'text/csv'
          },
          name: doc.name,
          type: 'csv',
          size: formatFileSizeHelper(doc.size || 0),
          progress: 0,
          status: 'ready',
          isOneDrive: true // Flag to identify OneDrive files
        }));

        // Replace any existing CSV files instead of adding to the array
        setMyVideos(prevFiles => {
          // Filter out any existing CSV files
          const nonCsvFiles = prevFiles.filter(file => file.type !== 'csv');
          // Add the new CSV file
          return [...nonCsvFiles, ...processedCsvFiles];
        });

        successMessage(`Selected CSV file: ${processedCsvFiles[0].name} from OneDrive`);
      }

      // Process video files
      if (oneDriveVideoFiles.length > 0) {
        // Process OneDrive video files in parallel to improve performance
        const processOneDriveFiles = async () => {
          setIsDownloadingOneDriveFiles(true);
          const totalFiles = oneDriveVideoFiles.length;
          let completedFiles = 0;

          // Initialize download status for each file
          const downloadStatus = {};
          oneDriveVideoFiles.forEach(doc => {
            downloadStatus[doc.id] = {
              progress: 0,
              status: 'pending',
              name: doc.name
            };
          });
          setOneDriveDownloads(downloadStatus);

          // Function to download a single file
          const downloadFile = async (doc) => {
            try {
              // Update download status
              setOneDriveDownloads(prev => ({
                ...prev,
                [doc.id]: { ...prev[doc.id], status: 'downloading', progress: 10 }
              }));

              // Show loading message
              successMessage(`Downloading ${doc.name} from OneDrive...`);

              // Actually download the file content from OneDrive
              const blob = await oneDriveService.downloadFile(doc.id);

              if (!blob) {
                throw new Error(`Failed to download ${doc.name} from OneDrive`);
              }

              // Update progress
              setOneDriveDownloads(prev => ({
                ...prev,
                [doc.id]: { ...prev[doc.id], progress: 50 }
              }));

              // Create a real file object with the downloaded content
              const realFile = new File([blob], doc.name, {
                type: doc.mimeType || 'video/mp4'
              });

              // Add isOneDrive flag and original ID to identify OneDrive files
              realFile.isOneDrive = true;
              realFile.oneDriveId = doc.id;

              // Add to video files list
              setVideoFiles(prevFiles => [...prevFiles, realFile]);

              // Create a real preview URL from the actual file content
              const previewUrl = URL.createObjectURL(blob);
              setVideoPreviews(prevPreviews => [...prevPreviews, previewUrl]);

              // Update status to completed
              setOneDriveDownloads(prev => ({
                ...prev,
                [doc.id]: { ...prev[doc.id], status: 'completed', progress: 100 }
              }));

              // Update completed count
              completedFiles++;

              // Inform the user of success
              successMessage(`Successfully loaded ${doc.name} from OneDrive (${completedFiles}/${totalFiles})`);

              return { success: true, docId: doc.id };
            } catch (error) {
              console.error('Error processing OneDrive file:', error);
              setError(`Error processing ${doc.name}: ${error.message}`);

              // Update status to failed
              setOneDriveDownloads(prev => ({
                ...prev,
                [doc.id]: { ...prev[doc.id], status: 'failed', progress: 0 }
              }));

              return { success: false, docId: doc.id, error };
            }
          };

          // Set up a limit for parallel downloads (typically 3-5 is a good balance)
          const MAX_CONCURRENT_DOWNLOADS = 3;

          // Process files in batches for controlled parallelism
          for (let i = 0; i < oneDriveVideoFiles.length; i += MAX_CONCURRENT_DOWNLOADS) {
            const batch = oneDriveVideoFiles.slice(i, i + MAX_CONCURRENT_DOWNLOADS);

            // Process this batch in parallel
            const results = await Promise.all(batch.map(doc => downloadFile(doc)));

            // Log results of this batch
            console.log(`Completed batch ${Math.floor(i / MAX_CONCURRENT_DOWNLOADS) + 1} of OneDrive downloads:`,
              results.map(r => r.success ? 'Success' : 'Failed'));
          }

          setIsDownloadingOneDriveFiles(false);
        };

        // Start processing files
        processOneDriveFiles();
      }

      // Save token and log selection
      let oneDriveToken = sessionStorage?.getItem('oneDriveToken');
      let obj = {
        user_id: getUser()?.id,
        files: data?.docs,
        oneDriveToken,
      };
      console.log('OneDrive files selected:', obj);
    }
  };

  const removeMyVideosFile = (name) => {
    setMyVideos(prev => prev.filter(file => file.name !== name));
  };

  const handleYoutubeUrlChange = (e) => {
    setYoutubeUrl(e.target.value);
    if (error && error.includes('YouTube')) {
      setError(null);
    }
  };

  const formatFileSizeHelper = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileIcon = (type, isOneDrive = false) => {
    const iconProps = { className: "w-5 h-5 text-gray-600" };
    if (type?.includes('csv') || type?.includes('spreadsheet')) {
      return (
        <div className="relative">
          <FileSpreadsheet {...iconProps} className="w-5 h-5 text-green-600" />
          {isOneDrive && (
            <Cloud className="absolute w-3 h-3 text-blue-500 -top-1 -right-1" />
          )}
        </div>
      );
    }

    switch (type) {
      case 'csv':
        return (
          <div className="relative">
            <FileSpreadsheet className="w-8 h-8 text-green-600" />
            {isOneDrive && (
              <Cloud className="absolute w-4 h-4 text-blue-500 -top-1 -right-1" />
            )}
          </div>
        );
      case 'video':
        return (
          <div className="relative">
            <Play className="w-8 h-8 text-blue-600" />
            {isOneDrive && (
              <Cloud className="absolute w-4 h-4 text-blue-500 -top-1 -right-1" />
            )}
          </div>
        );
      default:
        return (
          <div className="relative">
            <Video className="w-8 h-8 text-green-600" />
            {isOneDrive && (
              <Cloud className="absolute w-4 h-4 text-blue-500 -top-1 -right-1" />
            )}
          </div>
        );
    }
  };

  const resetForm = () => {
    if (inputType === 'upload') {
      setVideoFiles([]);
      setVideoPreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } else {
      setYoutubeUrl('');
    }
    setAdditionalInstructions('');
    setError(null);
    setPlayerIdForAnalysis('');
    setDropdownFolderId(null);
  };

  const handleAnalyzeVideo = async () => {
    if (!dropdownFolderId?.length) {
      return errorMessage('Please select a folder before uploading a video');
    }

    setIsAnalyzing(true);
    setIsDownloadingYoutube(inputType === 'youtube');
    setError(null);

    try {
      if (inputType === 'upload' && videoFiles.length === 0) {
        throw new Error('Please upload at least one video file');
      }
      if (inputType === 'youtube' && !youtubeUrl) {
        throw new Error('Please enter a valid YouTube URL');
      }

      if (inputType === 'youtube') {
        const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
        if (!youtubeRegex.test(youtubeUrl)) {
          throw new Error('Please enter a valid YouTube URL (e.g., https://www.youtube.com/watch?v=...)');
        }

        if (youtubeUrl.includes('youtube.com')) {
          const isWatchUrl = youtubeUrl.includes('/watch?v=');
          const isShortsUrl = youtubeUrl.includes('/shorts/');

          if (!isWatchUrl && !isShortsUrl) {
            throw new Error(
              'Please provide a valid YouTube watch or shorts URL (e.g., https://www.youtube.com/watch?v=videoId or https://www.youtube.com/shorts/videoId)'
            );
          }
        }

        if (youtubeUrl.includes('youtu.be') && youtubeUrl.split('/').pop() === '') {
          throw new Error('Please provide a valid shortened YouTube URL (e.g., https://youtu.be/videoId)');
        }

        if (!youtubeUrl.startsWith('http')) {
          setYoutubeUrl(`https://${youtubeUrl}`);
        }
      }

      let csvId = null;
      let csvName = null;
      const csvFile = myVideos.find(file => file.type === 'csv');

      if (csvFile) {
        try {
          successMessage(`Uploading CSV file: ${csvFile.name}...`);
          const csvFormData = new FormData();
          csvFormData.append('file', csvFile.file);

          const csvRes = await apiClient.post(
            `https://slam-all-python-359065791766.us-central1.run.app/${getSelectedSport()}/video-analysis/upload-csv`,
            csvFormData
          );

          const csvData = csvRes?.data;
          if (!csvData?.success) {
            throw new Error(`CSV upload failed for ${csvFile.name}`);
          }

          csvId = csvData.csv_id;
          csvName = csvFile.name;

          setMyVideos(prevFiles =>
            prevFiles.map(file =>
              file.id === csvFile.id ? { ...file, csv_id: csvId } : file
            )
          );

          successMessage(`CSV file uploaded successfully: ${csvFile.name}`);
        } catch (csvError) {
          console.error(`Error uploading CSV ${csvFile.name}:`, csvError);
          setError(`CSV upload error: ${csvError.message}`);
          setIsAnalyzing(false);
          return;
        }
      }

      let result;
      if (inputType === 'upload') {
        if (videoFiles.length > 1) {
          result = await analyzeMultipleVideos({
            videoFiles,
            instructions: additionalInstructions,
            player_id: playerIdForAnalysis,
            analysisTitle: "Biomechanics Analysis",
            folderId: dropdownFolderId,
            csvId: csvId,
            csvName: csvName
          });
        } else {
          result = await analyzeVideo({
            videoFile: videoFiles[0],
            youtubeUrl: null,
            instructions: additionalInstructions,
            player_id: playerIdForAnalysis,
            analysisTitle: "Biomechanics Analysis",
            folderId: dropdownFolderId,
            csvId: csvId,
            csvName: csvName
          });
        }
      } else {
        result = await analyzeVideo({
          videoFile: null,
          youtubeUrl: youtubeUrl,
          instructions: additionalInstructions,
          player_id: playerIdForAnalysis,
          analysisTitle: "Biomechanics Analysis",
          folderId: dropdownFolderId,
          csvId: csvId,
          csvName: csvName
        });
      }

      setVideoFiles([]);
      setIsAnalyzing(false);
      // successMessage('Upload started. Your videos are being processed.');
      resetForm();
      successMessage('Upload complete. Your videos are now being processed.');
      onUploadComplete()
    } catch (error) {
      console.error('Analysis error:', error);

      if (inputType === 'youtube' && error.message) {
        if (error.message.includes('timeout') || error.message.includes('timed out')) {
          setError('The YouTube video processing timed out. Please try a shorter video (under 5 minutes).');
        } else if (error.message.includes('video is too long')) {
          setError('The YouTube video is too long. Please use videos under 10 minutes for best results.');
        } else if (error.message.includes('private') || error.message.includes('unavailable')) {
          setError('This YouTube video is private or unavailable. Please try a different video.');
        } else if (error.message.includes('Could not extract') || error.message.includes('download')) {
          setError('Could not download YouTube video due to YouTube restrictions. Try a different video or upload a file directly.');
        } else {
          setError(`YouTube processing error: ${error.message}`);
        }
      } else {
        setError(error.message || 'An unexpected error occurred');
      }
    } finally {
      setIsAnalyzing(false);
      setIsDownloadingYoutube(false);
    }
  };

  return (
    <div
      style={{ width: '45rem' }}
      className={`fixed inset-y-0 right-0 z-50 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'
        } bg-[#0A0A0A] text-[#F5F5F5] shadow-lg overflow-y-auto`}
    >
      {/* Drawer Header */}
      <div className="px-4 py-3 border-b border-[#1A1A1A] flex justify-between items-center sticky top-0 bg-[#0A0A0A] z-10">
        <h2 className="text-lg font-semibold">{currentFolder ? `Folder: ${currentFolder}` : 'Biomechanics & Motion Capture'}</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-[rgba(255,255,255,0.04)]"
        >
          <X size={20} />
        </button>
      </div>

      {/* Drawer Content */}
      <div className="p-4">
        {/* Error message display */}
        {error && (
          <div className="mb-4 p-3 border rounded-[14px] flex items-start bg-red-900 border-red-700 text-red-200">
            <AlertTriangle size={24} className="mr-3 mt-0.5 flex-shrink-0" />
            <div>
              {typeof error === 'string'
                ? error.split('\n\n').map((paragraph, index) => (
                  <p key={index} className={index > 0 ? 'mt-2 text-sm' : 'font-semibold'}>
                    {paragraph}
                  </p>
                ))
                : <p className="font-semibold">{error.message}</p>
              }
            </div>
          </div>
        )}

        {/* Input Type Selector */}
        <div className="mb-6">
          <div className="flex items-center justify-center bg-[#0A0A0A] rounded-[14px] p-2 mx-auto shadow-sm">
            <button
              onClick={() => setInputType('upload')}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-[14px] font-medium transition-all duration-200 ${inputType === 'upload'
                ? 'bg-[#111111] text-[#E8B800] shadow-md'
                : 'text-[#999999] hover:text-[#F5F5F5]'
                }`}
            >
              <Upload size={18} className="mr-2" />
              Upload Video
            </button>
            <button
              onClick={() => setInputType('youtube')}
              className={`flex-1 flex items-center justify-center py-2 px-3 rounded-[14px] font-medium transition-all duration-200 ${inputType === 'youtube'
                ? 'bg-[#111111] text-red-400 shadow-md'
                : 'text-[#999999] hover:text-[#F5F5F5]'
                }`}
            >
              <Youtube size={18} className="mr-2" />
              YouTube URL
            </button>
            <OneDrivePicker
              onSelect={handleOneDriveSelect}
              acceptedFileTypes={['csv', 'mp4', 'mov']}
              multiSelect={true}
              customButtonRender={(onClick) => (
                <button
                  onClick={onClick}
                  ref={oneDrivePickerRef}
                  className="flex-1 flex items-center justify-center py-2 px-3 rounded-[14px] font-medium transition-all duration-200 text-[#999999] hover:text-[#F5F5F5]"
                >
                  <FolderOpen size={18} className="mr-2" />
                  OneDrive
                </button>
              )}
            />
          </div>
        </div>

        {/* Upload Section */}
        {inputType === 'upload' ? (
          <div className="space-y-4">
            {/* Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-[14px] p-14 text-center transition-all duration-200 cursor-pointer ${isDragOver
                ? 'border-[#E8B800] bg-[#E8B800]/10'
                : 'border-[#1A1A1A] bg-[#111111] hover:border-[#E8B800] hover:bg-[#E8B800]/10'
                }`}
              onClick={() => fileInputRef.current?.click()}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
            >
              <input
                type="file"
                ref={fileInputRef}
                accept="video/*,.csv"
                onChange={handleFileChange}
                className="hidden"
                multiple
              />

              <div className="space-y-3">
                {/* Upload Icon */}
                <div className={`mx-auto w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isDragOver
                  ? 'bg-[#E8B800]/20'
                  : 'bg-[#111111]'
                  }`}>
                  <CloudUpload size={22} className={
                    isDragOver
                      ? 'text-[#E8B800]'
                      : 'text-[#999999]'
                  } />
                </div>

                {/* Upload Text */}
                <div>
                  <h3 className="text-base font-semibold mb-2 text-[#F5F5F5]">
                    {isDragOver ? 'Drop your videos here' : 'Drag & drop your videos'}
                  </h3>
                  <p className="text-sm mb-3 text-[#999999]">
                    or <span className="font-medium text-[#E8B800]">browse from your device</span>
                  </p>

                  {/* Supported Formats */}
                  <div className="flex items-center justify-center space-x-3 text-xs text-[#555555]">
                    <div className="flex items-center">
                      <FileVideo size={14} className="mr-1" />
                      MP4
                    </div>
                    <div className="flex items-center">
                      <FileVideo size={14} className="mr-1" />
                      MOV
                    </div>
                    <div className="flex items-center">
                      <FileSpreadsheet size={14} className="mr-1" />
                      CSV
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* OneDrive Download Status */}
            {isDownloadingOneDriveFiles && Object.keys(oneDriveDownloads).length > 0 && (
              <div className="mb-4">
                <div className="p-3 rounded-[14px] bg-[#0A0A0A]">
                  <h5 className="mb-2 font-medium">OneDrive Download Progress</h5>
                  <div className="space-y-2">
                    {Object.entries(oneDriveDownloads).map(([fileId, download]) => (
                      <div key={fileId} className="flex flex-col">
                        <div className="flex justify-between mb-1 text-sm">
                          <span className="truncate">{download.name}</span>
                          <span>
                            {download.status === 'completed' ? (
                              <span className={`text-green-500 flex items-center`}>
                                <CheckCircle2 size={12} className="mr-1" /> Complete
                              </span>
                            ) : download.status === 'failed' ? (
                              <span className={`text-red-500 flex items-center`}>
                                <AlertTriangle size={12} className="mr-1" /> Failed
                              </span>
                            ) : (
                              <span>{download.progress}%</span>
                            )}
                          </span>
                        </div>
                        <div className="w-full bg-[#1A1A1A] rounded-full h-1.5 mb-1">
                          <div
                            className={`h-1.5 rounded-full ${download.status === 'failed' ? 'bg-red-500' : 'bg-blue-500'}`}
                            style={{ width: `${download.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* File Previews */}
            {videoPreviews.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-base font-semibold flex items-center text-[#F5F5F5]">
                  <CheckCircle2 size={18} className="text-green-400 mr-2" />
                  Selected Files ({videoPreviews.length})
                </h4>

                <div className="space-y-2 overflow-y-auto max-h-40">
                  {videoPreviews.map((preview, index) => (
                    <div key={`video-${index}`} className={`${'bg-gray-700'} rounded-lg p-2 flex items-center`}>
                      <div className="flex-shrink-0 w-12 h-12 mr-3 overflow-hidden rounded">
                        <video src={preview} className="object-cover w-full h-full" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate text-sm ${'text-white'}`}>
                          {videoFiles[index]?.name || `Video ${index + 1}`}
                        </p>
                        <p className={`text-xs ${'text-gray-400'}`}>
                          {formatFileSizeHelper(videoFiles[index]?.size || 0)}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveVideo(index);
                        }}
                        className={`p-1 rounded-full ${'hover:bg-gray-600'}`}
                        title="Remove video"
                      >
                        <X size={16} className={'text-gray-300'} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CSV Files */}
            {myVideos.filter(file => file.type === 'csv').length > 0 && (
              <div className="space-y-3">
                <h4 className={`text-base font-semibold flex items-center ${'text-white'}`}>
                  <FileSpreadsheet size={18} className="mr-2 text-green-600" />
                  CSV Files
                </h4>

                <div className="space-y-2">
                  {myVideos.filter(file => file.type === 'csv').map((csvFile) => (
                    <div key={`csv-${csvFile.id}`} className={`${'bg-gray-700'} rounded-lg p-2 flex items-center`}>
                      <FileSpreadsheet size={24} className="mr-3 text-green-600" />
                      <div className="flex-1 min-w-0">
                        <p className={`font-medium truncate text-sm ${'text-white'}`}>
                          {csvFile.name}
                        </p>
                        <p className={`text-xs ${'text-gray-400'}`}>
                          {csvFile.size}
                        </p>
                      </div>
                      <button
                        onClick={() => removeMyVideosFile(csvFile.name)}
                        className={`p-1 rounded-full ${'hover:bg-gray-600'}`}
                        title="Remove CSV"
                      >
                        <X size={16} className={'text-gray-300'} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          /* YouTube URL Section */
          <div className="space-y-4">
            <div>
              {/* YouTube Input */}
              <div className="relative">
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                  <Youtube size={18} className="text-red-500" />
                </div>
                <input
                  type="text"
                  placeholder="https://www.youtube.com/watch?v=..."
                  value={youtubeUrl}
                  onChange={handleYoutubeUrlChange}
                  className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    }`}
                />
              </div>

              {/* YouTube URL Validation */}
              {youtubeUrl && (
                <div className="flex items-center mt-2">
                  {youtubeUrl.includes('youtube.com') || youtubeUrl.includes('youtu.be') ? (
                    <div className={`flex items-center ${'text-green-400'}`}>
                      <CheckCircle2 size={14} className="mr-1" />
                      <span className="text-xs">Valid YouTube URL</span>
                    </div>
                  ) : (
                    <div className={`flex items-center ${'text-red-400'}`}>
                      <AlertTriangle size={14} className="mr-1" />
                      <span className="text-xs">Please enter a valid YouTube URL</span>
                    </div>
                  )}
                </div>
              )}

              {/* YouTube Tips */}
              <div className={`mt-4 border rounded-lg p-3 ${'bg-red-900/20 border-red-800'
                }`}>
                <h4 className={`text-sm font-medium mb-1 ${'text-red-300'}`}>
                  YouTube Video Requirements
                </h4>
                <ul className={`text-xs space-y-1 ${'text-red-400'}`}>
                  <li>• Video must be publicly available</li>
                  <li>• Recommended duration: under 5 minutes</li>
                  <li>• High-quality videos for better analysis</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Analysis Details */}
        <div className={`mt-6 border rounded-lg p-4 ${'bg-gray-700 border-gray-600'}`}>
          <h3 className={`text-base font-semibold mb-4 ${'text-blue-400'}`}>
            Analysis Details
          </h3>

          <div className="space-y-4">
            {/* Folder Selection */}
            <FolderDropdown
              selectedFolderId={dropdownFolderId}
              onFolderSelect={setDropdownFolderId}
              label="Select or Create Folder"
            />

            {/* Player ID */}
            <div>
              <label className={`block text-sm font-medium ${'text-gray-300'} mb-1`}>
                Player ID (Optional)
              </label>
              <select
                value={playerIdForAnalysis}
                onChange={(e) => setPlayerIdForAnalysis(e.target.value)}
                className={`appearance-none border w-full rounded-md pl-3 pr-10 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-200 focus:border-blue-200 ${'bg-gray-700 border-gray-600 text-white'}`}
              >
                <option value="" disabled>
                  Select a player (optional)
                </option>
                {allPlayers?.map((player) => (
                  <option key={player?.player_name}>
                    {player?.Player || player?.player_name || player?.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Additional Instructions */}
            <div>
              <label className={`block text-sm font-medium ${'text-gray-300'} mb-1`}>
                Analysis Instructions (Optional)
              </label>
              <textarea
                placeholder="e.g., Focus on the serve technique, analyze the defensive footwork..."
                className={`w-full p-2 border rounded-md h-20 focus:outline-none focus:ring-2 focus:ring-blue-600 resize-none ${'bg-gray-700 border-gray-600 text-white placeholder-gray-400'}`}
                value={additionalInstructions}
                onChange={(e) => setAdditionalInstructions(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Submit Button */}
        <button
          onClick={handleAnalyzeVideo}
          disabled={isAnalyzing || (inputType === 'upload' && videoFiles.length === 0) || (inputType === 'youtube' && !youtubeUrl)}
          className={`mt-6 w-full py-2 px-4 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center ${'bg-blue-600 hover:bg-blue-700'
            }`}
        >
          {isAnalyzing ? (
            <>
              <Loader size={18} className="mr-2 animate-spin" />
              Analyzing...
            </>
          ) : (
            <>
              <Video size={18} className="mr-2" />
              Analyze Video
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default BioMechanicsDrawer;